import React, { useEffect } from 'react';
import Modal from '../Modal';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { H2, H4, P } from '@osmosis/tailwind-ui/components/Text';
import ModalHeader from '../ModalHeader';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { faBolt } from '@fortawesome/pro-regular-svg-icons/faBolt';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons/faFileLines';
import { faMobile } from '@fortawesome/pro-regular-svg-icons/faMobile';
import { faStethoscope } from '@fortawesome/pro-regular-svg-icons/faStethoscope';

import CountdownTimer from './CountdownTimer';
import useModal from '../../hooks/useModal';

const getListItems = (intl: IntlShape) => [
  {
    icon: faYoutube,
    iconColor: 'primary',
    text: intl.formatMessage({
      defaultMessage: '1700+ medical videos',
      id: 'free_trial.modal.list_item.medical_videos',
    }),
  },
  {
    icon: faStethoscope,
    iconColor: 'lavender',
    text: intl.formatMessage({
      defaultMessage: '2700+ Step 1/Step 2 questions',
      id: 'free_trial.modal.list_item.questions',
    }),
  },
  {
    icon: faFileLines,
    iconColor: 'teal',
    text: intl.formatMessage({
      defaultMessage: '1200+ High Yield Notes',
      id: 'free_trial.modal.list_item.high_yield_notes',
    }),
  },
  {
    icon: faBolt,
    iconColor: 'orange',
    text: intl.formatMessage({
      defaultMessage: 'Custom flashcard builder with 15,000+ flashcards',
      id: 'free_trial.modal.list_item.flashcards',
    }),
  },
  {
    icon: faMobile,
    iconColor: 'gray60',
    text: intl.formatMessage({
      defaultMessage: 'Mobile app for iOS and Android',
      id: 'free_trial.modal.list_item.mobile',
    }),
  },
];

/**
 * Modal displaying that the free trial was activated.
 */
const FreeTrialActivatedModal = () => {
  const modal = useModal();
  const intl = useIntl();

  const items = getListItems(intl);

  useEffect(() => {
    modal.show();
  }, []);

  return (
    <Modal {...modal}>
      <ModalHeader
        onClose={modal.hide}
        headerNode={
          <H2 className="text-center">
            <FormattedMessage
              defaultMessage="Activated!"
              id="free_trial.modal.heading"
            />
          </H2>
        }
      />

      <Box className="p-6 w-[400px] max-w-full">
        <H4 className="text-center">
          <FormattedMessage
            defaultMessage="Your free trial of Osmosis Plan ends in:"
            id="free_trial.modal.subheading"
          />
        </H4>

        <CountdownTimer duration={7 * 24 * 60 * 60} />

        <Flex className="flex-col gap-4 w-3/4 mx-auto mt-6 mb-4">
          {items.map((item) => (
            <Flex key={item.text} className="gap-3 items-center max-w-[250px]">
              <Flex
                className="justify-center text-xl"
                style={{ color: item.iconColor }}
              >
                <Icon icon={item.icon} />
              </Flex>
              <P className="flex-1">{item.text}</P>
            </Flex>
          ))}
        </Flex>

        <Box className="mb-6">
          <P className="text-center opacity-90">
            <FormattedMessage
              defaultMessage="...and so much more!"
              id="free_trial.modal.more"
            />
          </P>
        </Box>

        <Flex className="justify-center">
          <Button
            onClick={modal.hide}
            data-testid="start-osmosing-btn"
            className="rounded-full px-6 py-2"
          >
            <FormattedMessage
              defaultMessage="Start Osmosing"
              id="free_trial.modal.start"
            />
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
};

export default FreeTrialActivatedModal;
